import type { FC } from 'react';
import { useModalDispatch } from '@hooks/useModalProvider';
import { getUsersTableColumns } from '@pages/User/helpers/columns/getUsersTableColumns';
import { useAllTeams } from '@queries/fetchTeams';
import { useUsers } from '@queries/fetchUsers';
import type { Action, PlainAction } from '@root/@types/types';
import type { CanonicalUserRecordWithCapabilities } from '@root/helpers/user-management/types';
import withMenuLayout from '@root/src/layout/withMenuLayout';
import { isPPLFrontend } from '@root/src/utils/app-paths';

import { MODALS } from '../../constants';

import { DownloadCSVButton } from './components/DownloadCSVButton';
import { ReactTable } from './components';
import { useDataIsEmpty } from './hooks';

export const Users: FC = () => {
  const { showModal } = useModalDispatch();

  const { data = [], status, refetch } = useUsers();

  const { data: teams = [], isLoading: isTeamsFetching } = useAllTeams();

  const isEmpty = useDataIsEmpty(data, status);

  const handleResendButtonClick = (user: CanonicalUserRecordWithCapabilities) => () =>
    showModal(MODALS.ADMIN_USER_RESEND_INVITE, { user, onRemoveUser: refetch });

  const handleEditUserClick = (user: CanonicalUserRecordWithCapabilities) => () =>
    showModal(MODALS.ADMIN_USER, { user, isAdd: false });

  const createEntityAction: PlainAction = {
    key: 'create',
    label: 'New User',
    onClick: () => showModal(MODALS.ADMIN_USER, { isAdd: true }),
  };

  const actions: Action[] = [
    createEntityAction,
    !isPPLFrontend && <DownloadCSVButton key="download-csv" type="users" />,
  ];

  const columns = getUsersTableColumns({ handleEditUserClick, handleResendButtonClick, teams });

  return (
    <ReactTable
      actions={actions}
      columns={columns}
      columnVisibility={{
        id: false,
        identityProvider: false,
      }}
      columnPinning={{ right: ['edit'] }}
      createEntityAction={createEntityAction}
      entityName="user"
      filteredData={data}
      initialSorting={[{ id: 'created_at', desc: true }]}
      status={status}
      isEmpty={isEmpty}
      isFetching={isTeamsFetching}
    />
  );
};

export const UserUsersPage = withMenuLayout(Users);
